/**
 * De vaste set redenen waarom een traject niet automatisch ingepland kon worden.
 *
 * Spiegelt API/AutoTransport.Domain/Enums/TrajectoryPlanFailureReason.cs. De numerieke waarden zijn
 * positioneel: wijzigt de backend-enum, dan moet deze mee.
 */
export enum TrajectoryPlanFailureReason {
  Unknown = 0,
  TooHeavy = 1,
  TooLong = 2,
  NoWinch = 3,
  TimeWindowInfeasible = 4,
  DidNotFit = 5,
  NoSolution = 6,
  PlannedElsewhere = 7,
  VehicleUnavailable = 8,
  TechnicalError = 9,
  MissingCoordinates = 10,
  // 11 was "gewicht onbekend"; sinds werkitem 7450 is het gewicht optioneel. Overgeslagen, niet hergebruikt.
  DataChanged = 12,
}

/** Spiegelt API/AutoTransport.Domain/Enums/TransportVehiclePlanIssue.cs. */
export enum TransportVehiclePlanIssue {
  Unavailable = 1,
  MissingStation = 2,
  StationWithoutCoordinates = 3,
  MissingMaxPayloadMass = 4,
}

/** Spiegelt API/AutoTransport.Domain/Enums/PlanRunFailureProfile.cs — de zes profielen uit werkitem 7375. */
export enum PlanRunFailureProfile {
  None = 0,
  /** V — de aanvraag is vóór verzenden afgekeurd. */
  Rejected = 1,
  /** W — tijdelijk niet beschikbaar; opnieuw proberen heeft zin. */
  Unavailable = 2,
  /** F — fataal; opnieuw proberen heeft geen zin. */
  Fatal = 3,
  /** T — geen resultaat gekomen, of de run is verloren. */
  TimedOut = 4,
  /** P — gedeeltelijk gelukt: niet alle trajecten zijn ingepland. */
  Partial = 5,
  /** Q — afstanden en rijtijden zijn (deels) geschat in plaats van berekend. */
  Estimated = 6,
  /** G — de gegevens zijn tijdens het rekenen gewijzigd; opnieuw plannen is de oplossing. */
  DataChanged = 7,
}

/**
 * De vertaalsleutel per reden. Eén tabel in plaats van een sleutel per aanroep: de reden komt op drie plekken
 * in beeld (de samenvattings-dialoog, de badge in de trajectenlijst en de controle vóór verzenden) en die
 * moeten dezelfde woorden gebruiken.
 */
const REASON_KEYS: Record<TrajectoryPlanFailureReason, string> = {
  [TrajectoryPlanFailureReason.Unknown]: 'unknown',
  [TrajectoryPlanFailureReason.TooHeavy]: 'too_heavy',
  [TrajectoryPlanFailureReason.TooLong]: 'too_long',
  [TrajectoryPlanFailureReason.NoWinch]: 'no_winch',
  [TrajectoryPlanFailureReason.TimeWindowInfeasible]: 'time_window_infeasible',
  [TrajectoryPlanFailureReason.DidNotFit]: 'did_not_fit',
  [TrajectoryPlanFailureReason.NoSolution]: 'no_solution',
  [TrajectoryPlanFailureReason.PlannedElsewhere]: 'planned_elsewhere',
  [TrajectoryPlanFailureReason.VehicleUnavailable]: 'vehicle_unavailable',
  [TrajectoryPlanFailureReason.TechnicalError]: 'technical_error',
  [TrajectoryPlanFailureReason.MissingCoordinates]: 'missing_coordinates',
  [TrajectoryPlanFailureReason.DataChanged]: 'data_changed',
};

const VEHICLE_ISSUE_KEYS: Record<TransportVehiclePlanIssue, string> = {
  [TransportVehiclePlanIssue.Unavailable]: 'unavailable',
  [TransportVehiclePlanIssue.MissingStation]: 'missing_station',
  [TransportVehiclePlanIssue.StationWithoutCoordinates]: 'station_without_coordinates',
  [TransportVehiclePlanIssue.MissingMaxPayloadMass]: 'missing_max_payload_mass',
};

/** Een reden die de app niet kent valt terug op "onbekend"; nooit op een lege regel. */
export const planFailureReasonKey = (reason: TrajectoryPlanFailureReason | null | undefined): string =>
  `components.transport.dayplanner.plan_run.reason.${
    (reason != null && REASON_KEYS[reason]) || REASON_KEYS[TrajectoryPlanFailureReason.Unknown]
  }`;

/**
 * De korte variant, voor in een tabelcel. Dezelfde sleutels onder een eigen kop, zodat de twee varianten van
 * dezelfde reden niet uit de pas kunnen lopen: een nieuwe reden dwingt beide teksten af.
 */
export const planFailureReasonShortKey = (reason: TrajectoryPlanFailureReason | null | undefined): string =>
  `components.transport.dayplanner.plan_run.reason_short.${
    (reason != null && REASON_KEYS[reason]) || REASON_KEYS[TrajectoryPlanFailureReason.Unknown]
  }`;

export const transportVehiclePlanIssueKey = (issue: TransportVehiclePlanIssue): string =>
  `components.transport.dayplanner.plan_run.vehicle_issue.${
    VEHICLE_ISSUE_KEYS[issue] ?? VEHICLE_ISSUE_KEYS[TransportVehiclePlanIssue.Unavailable]
  }`;

/**
 * Redenen waar de planner zelf iets aan kan doen door de gegevens te repareren, tegenover redenen die over de
 * planning van deze dag gaan. Bepaalt of de dialoog een link naar de opdracht aanbiedt: bij "paste er niet
 * bij" valt er niets te repareren, bij "geen coördinaten" wél.
 */
export const isRepairableReason = (reason: TrajectoryPlanFailureReason): boolean =>
  reason === TrajectoryPlanFailureReason.MissingCoordinates
  || reason === TrajectoryPlanFailureReason.TooHeavy
  || reason === TrajectoryPlanFailureReason.TooLong
  || reason === TrajectoryPlanFailureReason.TimeWindowInfeasible;
